import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { BatchMetafieldEntry } from "../services/shopifyApi";

export type DraftMode = "inventory" | "entry" | "exit" | "transfer";

export interface DraftChange {
  id: string;
  price?: number;
  qte: number; // delta pour Shopify/logs (entry: +, exit: -, inventory: target-current)
  name: string;
  vendor?: string;
  sku?: string;
  floorStock?: number | null;
  reserveStock?: number | null;
  batches?: BatchMetafieldEntry[];
}

export interface DraftPayload {
  date?: string; // ISO string if set
  location: string;
  toLocationId?: string;
  changes: DraftChange[];
  type: DraftMode;
  comment?: string;
  uiMode?: "simple" | "split"; // préférence d'affichage pour le mode inventaire
}

export type DraftsByLocation = Record<string, DraftPayload>;

export interface InventoryDraftState {
  inventory: DraftsByLocation;
  entry: DraftsByLocation;
  exit: DraftsByLocation;
  transfer: DraftsByLocation;
}

const initialState: InventoryDraftState = {
  inventory: {},
  entry: {},
  exit: {},
  transfer: {},
};

interface UpsertChangePayload {
  mode: DraftMode;
  location: string;
  change: DraftChange;
}

interface RemoveChangePayload {
  mode: DraftMode;
  location: string;
  productId: string;
}

interface SetMetaPayload {
  mode: DraftMode;
  location: string; // location requis
  toLocationId?: string;
  comment?: string;
  date?: string; // ISO
  uiMode?: "simple" | "split";
}

const findIndexById = (changes: DraftChange[], id: string) =>
  changes.findIndex((c) => c.id === id);

const ensureDraft = (
  state: InventoryDraftState,
  mode: DraftMode,
  location: string
): DraftPayload => {
  if (!state[mode]) {
    (state as any)[mode] = {};
  }
  const modeBucket = state[mode];
  if (!modeBucket[location]) {
    modeBucket[location] = {
      location,
      changes: [],
      type: mode,
      comment: "",
    };
  }
  return modeBucket[location];
};

const inventoryDraftSlice = createSlice({
  name: "inventoryDrafts",
  initialState,
  reducers: {
    setDraft(
      state,
      action: PayloadAction<{ mode: DraftMode; location: string; payload: DraftPayload }>
    ) {
      const { mode, location, payload } = action.payload;
      if (!state[mode]) {
        (state as any)[mode] = {};
      }
      state[mode][location] = payload;
    },
    clearDraft(state, action: PayloadAction<{ mode: DraftMode; location: string }>) {
      const { mode, location } = action.payload;
      if (!state[mode]) {
        (state as any)[mode] = {};
      }
      if (state[mode][location]) {
        delete state[mode][location];
      }
    },
    upsertChange(state, action: PayloadAction<UpsertChangePayload>) {
      const { mode, location, change } = action.payload;
      const target = ensureDraft(state, mode, location);

      // If qte is 0 and no split provided, remove the change entry
      const hasBatches = change.batches && change.batches.length > 0;
      const shouldRemove =
        !hasBatches &&
        (change.qte === 0 || Number.isNaN(change.qte)) &&
        (change.floorStock === undefined || change.floorStock === null) &&
        (change.reserveStock === undefined || change.reserveStock === null);

      const idx = findIndexById(target.changes, change.id);

      if (shouldRemove) {
        if (idx !== -1) target.changes.splice(idx, 1);
        return;
      }

      if (idx === -1) {
        target.changes.push(change);
      } else {
        target.changes[idx] = { ...target.changes[idx], ...change };
      }
    },
    removeChange(state, action: PayloadAction<RemoveChangePayload>) {
      const { mode, location, productId } = action.payload;
      const target = ensureDraft(state, mode, location);
      target.changes = target.changes.filter((c) => c.id !== productId);
    },
    setMeta(state, action: PayloadAction<SetMetaPayload>) {
      const { mode, location, comment, date, toLocationId } = action.payload;
      const target = ensureDraft(state, mode, location);
      if (comment !== undefined) target.comment = comment;
      if (date !== undefined) target.date = date;
      if (toLocationId !== undefined) target.toLocationId = toLocationId;
      if (action.payload.uiMode !== undefined) target.uiMode = action.payload.uiMode;
    },
  },
});

export const { setDraft, clearDraft, upsertChange, removeChange, setMeta } =
  inventoryDraftSlice.actions;

export default inventoryDraftSlice.reducer;
